import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import MeditationRoom, { ROOM_ART_KEYS, ROOM_PRACTICE_LABELS, RoomPractice } from '@/models/meditation-room';
import { CreateRoomPayload, useDeleteMeditationRoom, useSaveMeditationRoom } from '@/services/meditation-rooms';
import {
    addButtonStyle,
    dangerButtonStyle,
    darkBorder,
    darkText,
    darkTextDim,
    darkWarningText,
    errorBannerStyle,
    plainButtonStyle,
    warningBannerStyle,
} from '@/shared/dark-inline-styles';

const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'transparent',
    border: `1px solid ${darkBorder}`,
    borderRadius: 4,
    color: darkText,
    padding: '8px 10px',
    fontSize: 13,
};

const labelStyle: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: darkTextDim, margin: '0 0 4px' };

/** Lowercase slug, matching the API's validator exactly so nothing 400s here. */
const KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

/**
 * Create or edit one room.
 *
 * `key` is editable ONLY on create, and the modal says why rather than just
 * disabling the field. It is what every `meditationAttendance` row points at, so
 * changing it after the fact would orphan every sit ever recorded here — the
 * room would report zero and everybody's rank would reset, with nothing logged
 * and no error. The API refuses the field on update; this is the second lock.
 */
const RoomModal = ({ room, onClose }: { room: MeditationRoom | null; onClose: () => void }) => {
    const isEdit = !!room;
    const save = useSaveMeditationRoom();
    const remove = useDeleteMeditationRoom();
    const [error, setError] = useState<string | null>(null);

    const [form, setForm] = useState<CreateRoomPayload>({
        key: room?.key ?? '',
        name: room?.name ?? '',
        tagline: room?.tagline ?? '',
        practice: room?.practice ?? RoomPractice.Timer,
        categoryId: room?.categoryId ?? '',
        artKey: room?.artKey ?? 'candle',
        orderIndex: room?.orderIndex ?? 0,
        active: room?.active ?? true,
    });

    const set = <K extends keyof CreateRoomPayload>(field: K, value: CreateRoomPayload[K]) => setForm((prev) => ({ ...prev, [field]: value }));

    const submit = async () => {
        setError(null);

        if (!form.name.trim()) return setError('The room needs a name.');
        if (!isEdit && !KEY_PATTERN.test(form.key)) {
            return setError('The key must be a lowercase slug — letters, numbers and hyphens, e.g. "vipassana".');
        }

        // Empty optional strings are omitted rather than sent as "": the API
        // rejects unmatched keys and an empty categoryId is not a category.
        const payload = {
            name: form.name.trim(),
            practice: form.practice,
            artKey: form.artKey,
            orderIndex: Number(form.orderIndex) || 0,
            active: form.active,
            ...(form.tagline?.trim() ? { tagline: form.tagline.trim() } : {}),
            ...(form.categoryId?.trim() ? { categoryId: form.categoryId.trim() } : {}),
            ...(isEdit ? {} : { key: form.key.trim() }),
        } as CreateRoomPayload;

        try {
            await save.mutateAsync({ id: room?.id, payload });
            onClose();
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not save the room.');
        }
    };

    const onDelete = async () => {
        if (!room) return;
        try {
            await remove.mutateAsync(room.id);
            onClose();
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not remove the room.');
        }
    };

    return (
        <Modal show onHide={onClose} centered size="lg" data-bs-theme="dark">
            <Modal.Header closeButton>
                <Modal.Title style={{ color: darkText }}>{isEdit ? room?.name : 'New room'}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && <div style={errorBannerStyle}>{error}</div>}

                <div style={{ display: 'grid', gap: 14 }}>
                    <div>
                        <p style={labelStyle}>Key</p>
                        <input
                            style={{ ...inputStyle, opacity: isEdit ? 0.55 : 1 }}
                            value={form.key}
                            disabled={isEdit}
                            placeholder="vipassana"
                            onChange={(event) => set('key', event.target.value.toLowerCase())}
                        />
                        {isEdit ? (
                            <div style={{ ...warningBannerStyle, marginTop: 8, fontSize: 12 }}>
                                The key is permanent. Every sit ever recorded in this room points at it, so changing it would orphan the whole history
                                — the room would report zero and everybody&apos;s rank would reset. Rename the room instead.
                            </div>
                        ) : (
                            <p style={{ color: darkWarningText, fontSize: 12, marginTop: 6 }}>
                                Choose carefully — this can never be changed, and it is what every recorded sit will point at.
                            </p>
                        )}
                    </div>

                    <div>
                        <p style={labelStyle}>Name</p>
                        <input style={inputStyle} value={form.name} placeholder="Vipassana" onChange={(event) => set('name', event.target.value)} />
                    </div>

                    <div>
                        <p style={labelStyle}>Tagline</p>
                        <input
                            style={inputStyle}
                            value={form.tagline ?? ''}
                            placeholder="Sit in silence"
                            onChange={(event) => set('tagline', event.target.value)}
                        />
                    </div>

                    <div>
                        <p style={labelStyle}>Practice</p>
                        <select style={inputStyle} value={form.practice} onChange={(event) => set('practice', event.target.value as RoomPractice)}>
                            {Object.values(RoomPractice).map((practice) => (
                                <option key={practice} value={practice}>
                                    {ROOM_PRACTICE_LABELS[practice]}
                                </option>
                            ))}
                        </select>
                        <p style={{ color: darkTextDim, fontSize: 12, marginTop: 6 }}>
                            A silent room runs the timer with its own length and bell. A guided room lets the member pick a content.
                        </p>
                    </div>

                    <div>
                        <p style={labelStyle}>Mark</p>
                        <select style={inputStyle} value={form.artKey} onChange={(event) => set('artKey', event.target.value)}>
                            {ROOM_ART_KEYS.map((key) => (
                                <option key={key} value={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                        <p style={{ color: darkTextDim, fontSize: 12, marginTop: 6 }}>
                            Drawn in the app, so there is nothing to upload and nothing that can fail to load.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <p style={labelStyle}>Order</p>
                            <input
                                style={inputStyle}
                                type="number"
                                value={form.orderIndex ?? 0}
                                onChange={(event) => set('orderIndex', Number(event.target.value))}
                            />
                        </div>
                        <div>
                            <p style={labelStyle}>Visible</p>
                            <label style={{ color: darkText, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                <input type="checkbox" checked={!!form.active} onChange={(event) => set('active', event.target.checked)} />
                                Show in the app
                            </label>
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                {isEdit ? (
                    <button type="button" style={dangerButtonStyle} onClick={onDelete} disabled={remove.isPending}>
                        Remove
                    </button>
                ) : (
                    <span />
                )}
                <span style={{ display: 'flex', gap: 10 }}>
                    <button type="button" style={plainButtonStyle} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" style={addButtonStyle} onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving…' : 'Save'}
                    </button>
                </span>
            </Modal.Footer>
        </Modal>
    );
};

export default RoomModal;
